import React, { useEffect, useState } from "react";
import { googleLogout } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import "./Profile.css";
import { Playlist } from "../../types/PlaylistTypes";
import { UserReviewWithSongDetails } from "../../types/ReviewTypes";
import { encodeId } from "../../components/HelperMethods";
import StarRating from "../../components/Common/StarRating";
import "../../components/Common/StarRating.css";
import PhotoUploadModal from '../../components/Profile/PhotoUploadModal';

const ProfilePage: React.FC = () => {
    const [user, setUser] = useState<any>(null);
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [songReviews, setSongReviews] = useState<UserReviewWithSongDetails[]>([]);
    const [albumReviews, setAlbumReviews] = useState<any[]>([]);
    const [activeReviewTab, setActiveReviewTab] = useState<'songs' | 'albums'>('songs');
    const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(false);
    const [isLoadingSongReviews, setIsLoadingSongReviews] = useState(false);
    const [isLoadingAlbumReviews, setIsLoadingAlbumReviews] = useState(false);
    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [newUsername, setNewUsername] = useState("");
    const [usernameError, setUsernameError] = useState("");
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [isUpdatingUsername, setIsUpdatingUsername] = useState(false);
    const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Load user session on page load
        const storedUser = localStorage.getItem("user");

        if (storedUser) {
            setUser(JSON.parse(storedUser));
            fetchPlaylists();
            fetchSongReviews();
            fetchAlbumReviews();
        } else {
            // Redirect to login if not logged in
            navigate("/login");
        }
    }, [navigate]);

    const fetchPlaylists = async () => {
        setIsLoadingPlaylists(true);
        try {
            // Check if user data exists
            const storedUser = localStorage.getItem("user");
            console.log("User data exists:", !!storedUser);
            if (storedUser) {
                const userData = JSON.parse(storedUser);
                console.log("Full user data:", userData);
                console.log("User ID:", userData.userId);
                console.log("Dubu user:", userData.dubu_user);
            }

            // Log what cookies might be available
            console.log("Document cookies:", document.cookie);

            // Prepare headers
            const headers: HeadersInit = {
                "Content-Type": "application/json",
            };

            console.log("Request headers:", headers);

            const response = await fetch("/api/playlists/mine", {
                method: "GET",
                headers,
                credentials: "include", // Include cookies in the request
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Error response details:", {
                    status: response.status,
                    statusText: response.statusText,
                    headers: Object.fromEntries(response.headers.entries()),
                    body: errorText
                });
                throw new Error(`Failed to fetch playlists: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            setPlaylists(data);
        } catch (error) {
            console.error("Error fetching playlists:", error);
        } finally {
            setIsLoadingPlaylists(false);
        }
    };

    const fetchSongReviews = async () => {
        setIsLoadingSongReviews(true);
        try {
            const headers: HeadersInit = {
                "Content-Type": "application/json",
            };

            const response = await fetch("/api/reviews/user/withSongDetails", {
                method: "GET",
                headers,
                credentials: "include",
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Error fetching song reviews:", {
                    status: response.status,
                    statusText: response.statusText,
                    body: errorText
                });
                throw new Error(`Failed to fetch song reviews: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            setSongReviews(data);
        } catch (error) {
            console.error("Error fetching song reviews:", error);
        } finally {
            setIsLoadingSongReviews(false);
        }
    };

    const fetchAlbumReviews = async () => {
        setIsLoadingAlbumReviews(true);
        try {
            const headers: HeadersInit = {
                "Content-Type": "application/json",
            };

            const response = await fetch("/api/reviews/user/withAlbumDetails", {
                method: "GET",
                headers,
                credentials: "include",
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Error fetching album reviews:", {
                    status: response.status,
                    statusText: response.statusText,
                    body: errorText
                });
                throw new Error(`Failed to fetch album reviews: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            setAlbumReviews(data);
        } catch (error) {
            console.error("Error fetching album reviews:", error);
        } finally {
            setIsLoadingAlbumReviews(false);
        }
    };

    const handleStartEditUsername = () => {
        setIsEditingUsername(true);
        setNewUsername(user.dubu_user || "");
        setUsernameError("");
    };

    const handleCancelEditUsername = () => {
        setIsEditingUsername(false);
        setNewUsername("");
        setUsernameError("");
    };

    const checkUsernameAvailability = async (username: string) => {
        if (!username || username === user.dubu_user) {
            return true; // No change or empty username
        }

        setIsCheckingUsername(true);
        try {
            const response = await fetch(`/api/users/check-username/${username}`, {
                method: "GET",
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error(`Failed to check username: ${response.status}`);
            }

            const data = await response.json();
            return !data.exists;
        } catch (error) {
            console.error("Error checking username:", error);
            return false;
        } finally {
            setIsCheckingUsername(false);
        }
    };

    const handleUsernameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewUsername(value);

        // Basic validation
        if (!value.trim()) {
            setUsernameError("Username cannot be empty");
            return;
        }

        if (value.length < 3) {
            setUsernameError("Username must be at least 3 characters");
            return;
        }

        if (!/^[a-zA-Z0-9-_]+$/.test(value)) {
            setUsernameError("Username can only contain letters, numbers, hyphens, and underscores");
            return;
        }

        setUsernameError("");
    };

    const handleUpdateUsername = async () => {
        if (usernameError || !newUsername || newUsername === user.dubu_user) {
            return;
        }

        // Check availability before updating
        const isAvailable = await checkUsernameAvailability(newUsername);
        if (!isAvailable) {
            setUsernameError("Username already taken");
            return;
        }

        setIsUpdatingUsername(true);
        try {
            const response = await fetch(`/api/users/${user.userId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ dubu_user: newUsername }),
                credentials: "include",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to update username");
            }

            const updatedUser = await response.json();
            console.log('Server response:', updatedUser);

            // Update local storage with new user data
            const userData = { ...user, dubu_user: updatedUser.dubu_user };
            localStorage.setItem("user", JSON.stringify(userData));
            setUser(userData);

            setIsEditingUsername(false);
        } catch (error) {
            console.error("Error updating username:", error);
            if (error instanceof Error) {
                setUsernameError(error.message);
            } else {
                setUsernameError("An unknown error occurred");
            }
        } finally {
            setIsUpdatingUsername(false);
        }
    };

    const handleLogout = async () => {
        try {
            // Clear frontend stored data first
            setUser(null);
            localStorage.removeItem("token");
            localStorage.removeItem("user");

            // Dispatch custom event for login state change
            window.dispatchEvent(new Event('loginStateChange'));

            // Call backend to remove the token cookie
            const response = await fetch("/api/auth/logout", {
                method: "POST",
                credentials: "include", // Include cookies in the request
            });

            if (!response.ok) {
                throw new Error(`Logout failed with status: ${response.status}`);
            }

            console.log("Logged out successfully");

            // Call Google logout last
            try {
                googleLogout();
            } catch (googleError) {
                console.log("Google logout error (non-critical):", googleError);
            }

            // Navigate back to login page
            navigate("/login");

        } catch (error) {
            console.error("Error during logout:", error);
        }
    };

    // Add a checkAuth function to verify authentication status
    const checkAuth = async () => {
        try {
            const token = localStorage.getItem("token");
            const headers: HeadersInit = {
                "Content-Type": "application/json",
            };

            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            const response = await fetch("/api/auth/check", {
                method: "GET",
                headers,
                credentials: "include",
            });

            const data = await response.json();
            console.log("Auth check response:", data);

            if (response.ok) {
                console.log("Authentication valid, user:", data.user);
                alert("Authentication valid!");
                // Try fetching playlists again
                fetchPlaylists();
            } else {
                console.error("Authentication failed:", data);
                alert("Authentication failed: " + data.message);
            }
        } catch (error) {
            console.error("Error checking auth:", error);
            alert("Error checking authentication");
        }
    };

    const handlePhotoUpload = async (file: File) => {
        try {
            const formData = new FormData();
            formData.append('photo', file);

            const response = await fetch(`/api/users/${user.userId}/photo`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Failed to upload photo');
            }

            const updatedUser = await response.json();
            console.log('Server response:', updatedUser);

            // Add cache-busting parameter to the photo URL
            const photoUrlWithCacheBust = `${updatedUser.picture}?t=${new Date().getTime()}`;
            const userData = { ...user, picture: photoUrlWithCacheBust };

            // Update user in localStorage with new photo URL
            localStorage.setItem('user', JSON.stringify(userData));

            // Update local state
            setUser(userData);

            // Dispatch a custom event for photo update with cache-busting URL
            const photoUpdateEvent = new CustomEvent('photoUpdate', {
                detail: { picture: photoUrlWithCacheBust }
            });
            window.dispatchEvent(photoUpdateEvent);

            // Close the modal
            setIsPhotoModalOpen(false);
        } catch (error) {
            console.error('Error uploading photo:', error);
            throw error;
        }
    };

    if (!user) {
        return <div>Loading...</div>;
    }

    return (
        <div className="padding">
            <div className="profile-page">
                <div className="profile-content">
                    <div className="row-1">
                        <div className="profile-container">
                            <div className="profile-header">
                                <div className="profile-account-info">
                                    <img
                                        src={user.picture}
                                        className="profile-image"
                                        onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                                            event.currentTarget.src = "/dubuFace260.jpg";
                                            event.currentTarget.onerror = null;
                                        }}
                                        onClick={() => setIsPhotoModalOpen(true)}
                                    />
                                    <button
                                        className="edit-photo-button"
                                        onClick={() => setIsPhotoModalOpen(true)}
                                    >
                                        <i className="fa fa-camera" aria-hidden="true"></i> EDIT PHOTO
                                    </button>
                                </div>
                                <h3>welcome,
                                    {isEditingUsername ? (
                                        <div className="username-edit-container">
                                            <input
                                                type="text"
                                                value={newUsername}
                                                onChange={handleUsernameChange}
                                                className={`username-input ${usernameError ? 'username-input-error' : ''}`}
                                                placeholder="Enter new username"
                                                disabled={isUpdatingUsername}
                                            />
                                            {usernameError && <p className="username-error">{usernameError}</p>}
                                            <div className="username-buttons">
                                                <button
                                                    onClick={handleUpdateUsername}
                                                    className="username-save-button"
                                                    disabled={!!usernameError || isUpdatingUsername || isCheckingUsername || !newUsername || newUsername === user.dubu_user}
                                                >
                                                    {isUpdatingUsername ? 'saving...' : 'save'}
                                                </button>
                                                <button
                                                    onClick={handleCancelEditUsername}
                                                    className="username-cancel-button"
                                                    disabled={isUpdatingUsername}
                                                >
                                                    cancel
                                                </button>
                                            </div>
                                        </div>

                                    ) : (
                                        <span className="username-display">{user.dubu_user}</span>
                                    )}
                                </h3>
                                <button
                                    onClick={handleStartEditUsername}
                                    className="edit-username-button"
                                >
                                    <i className="fa fa-pencil" aria-hidden="true"></i> EDIT USERNAME
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="row-2">
                        <div className="review-container-profile">
                            <div className="review-section-header">
                                <h3>my reviews ({songReviews.length + albumReviews.length})</h3>
                                <div className="review-tabs">
                                    <button
                                        className={`review-tab ${activeReviewTab === 'songs' ? 'active' : ''}`}
                                        onClick={() => setActiveReviewTab('songs')}
                                    >
                                        songs ({songReviews.length})
                                    </button>
                                    <button
                                        className={`review-tab ${activeReviewTab === 'albums' ? 'active' : ''}`}
                                        onClick={() => setActiveReviewTab('albums')}
                                    >
                                        albums ({albumReviews.length})
                                    </button>
                                </div>
                            </div>

                            {activeReviewTab === 'songs' ? (
                                isLoadingSongReviews ? (
                                    <p>Loading song reviews...</p>
                                ) : songReviews.length === 0 ? (
                                    <p>No song reviews found. Start reviewing songs to see them here!</p>
                                ) : (
                                    <div className="review-list">
                                        {songReviews.map((review) => (
                                            <div
                                                key={review.id}
                                                className="review-item-profile"
                                                onClick={() => navigate(`/review/${review.id}`)}
                                            >
                                                <div className="review-item-content">
                                                    <div className="review-album-image">
                                                        <img
                                                            src={review.album_picture_url || "/dubuFace.jpg"}
                                                            alt={review.song_name}
                                                            onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                                                                event.currentTarget.src = "/dubuFace.jpg";
                                                                event.currentTarget.onerror = null;
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="review-details">
                                                        <div className="profile-review-header">
                                                            <h5>{review.song_name}</h5>
                                                            <StarRating rating={Number(review.rating)} size="small" />
                                                        </div>
                                                        <p className="review-artist">
                                                            {review.artists && review.artists.map((artist: any, index: number) => (
                                                                <React.Fragment key={artist.artist_id}>
                                                                    <span
                                                                        className="artist-link"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate(`/artist/${encodeId(artist.artist_id)}`);
                                                                        }}
                                                                    >
                                                                        {artist.artist_name}
                                                                    </span>
                                                                    {index < review.artists.length - 1 && ", "}
                                                                </React.Fragment>
                                                            ))}
                                                        </p>
                                                        <p className="review-content-preview">
                                                            {review.content.length > 50
                                                                ? `${review.content.substring(0, 50)}...`
                                                                : review.content}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            ) : (
                                isLoadingAlbumReviews ? (
                                    <p>Loading album reviews...</p>
                                ) : albumReviews.length === 0 ? (
                                    <p>No album reviews found. Start reviewing albums to see them here!</p>
                                ) : (
                                    <div className="review-list">
                                        {albumReviews.map((review) => (
                                            <div
                                                key={review.id}
                                                className="review-item-profile"
                                                onClick={() => navigate(`/review/album-${review.id}`)}
                                            >
                                                <div className="review-item-content">
                                                    <div className="review-album-image">
                                                        <img
                                                            src={review.album_picture_url || "/dubuFace.jpg"}
                                                            alt={review.album_name}
                                                            onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                                                                event.currentTarget.src = "/dubuFace.jpg";
                                                                event.currentTarget.onerror = null;
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="review-details">
                                                        <div className="profile-review-header">
                                                            <h5>{review.album_name}</h5>
                                                            <StarRating rating={Number(review.rating)} size="small" />
                                                        </div>
                                                        <p className="review-artist">
                                                            {review.artists && review.artists.map((artist: any, index: number) => (
                                                                <React.Fragment key={artist.artist_id}>
                                                                    <span
                                                                        className="artist-link"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate(`/artist/${encodeId(artist.artist_id)}`);
                                                                        }}
                                                                    >
                                                                        {artist.artist_name}
                                                                    </span>
                                                                    {index < review.artists.length - 1 && ", "}
                                                                </React.Fragment>
                                                            ))}
                                                        </p>
                                                        <p className="review-content-preview">
                                                            {review.content.length > 50
                                                                ? `${review.content.substring(0, 50)}...`
                                                                : review.content}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                        <div className="playlist-container-profile">
                            <div className="playlist-section-header">
                                <h3>my playlists</h3>
                                <button
                                    className="create-playlist-button"
                                    onClick={() => navigate('/create-playlist')}
                                >
                                    <i className="fa fa-plus"></i>
                                </button>
                            </div>
                            {isLoadingPlaylists ? (
                                <p>Loading playlists...</p>
                            ) : playlists.length === 0 ? (
                                <p>No playlists found. Create one to get started!</p>
                            ) : (
                                <div className="playlist-list">
                                    {playlists.map((playlist) => (
                                        <div key={playlist._id} className="playlist-item" onClick={() => navigate(`/playlist/${playlist._id}`)}>
                                            <h5>{playlist.name}</h5>
                                            {/* {playlist.description && <p>{playlist.description}</p>} */}
                                            <p className="playlist-visibility">{playlist.is_public ? "Public" : "Private"}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <button onClick={handleLogout} className="logout-button">
                        logout
                    </button>
                </div>
            </div>

            <PhotoUploadModal
                isOpen={isPhotoModalOpen}
                onClose={() => setIsPhotoModalOpen(false)}
                onUpload={handlePhotoUpload}
                currentPhotoUrl={user.picture}
            />
        </div>
    );
};

export default ProfilePage;
